import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Video, Clock, X } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../i18n/LanguageContext";

const formatCountdown = (mins, t) => {
  if (mins === null || mins === undefined) return "";
  if (mins <= 0) return t("classReminderBanner.startingNow");
  if (mins < 60) return t("classReminderBanner.inMinutes", { mins });
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return t("classReminderBanner.inHoursMinutes", { h, m });
};

export default function ClassReminderBanner() {
  const { t } = useLanguage();
  const [reminders, setReminders] = useState([]);
  const [dismissed, setDismissed] = useState({});

  const load = () =>
    api.get("/notifications").then((r) => setReminders(r.data)).catch(() => {});

  useEffect(() => {
    load();
    const id = setInterval(load, 60_000); // refresh every minute
    const onVisible = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  const join = async (r) => {
    try {
      await api.post("/classes/attend", { class_id: r.class_id });
      toast.success(t("classReminderBanner.attendanceMarkedSeeYouIn"));
    } catch (e) {
      // Soft fail — still let them open Zoom even if attendance window is off
      toast.info(e?.response?.data?.detail || t("halaqa.joiningZoom"));
    }
    window.open(r.zoom_url, "_blank", "noopener");
  };

  const visible = reminders.filter((r) => !dismissed[r.class_id]);
  if (visible.length === 0) return null;

  return (
    <div className="space-y-3" data-testid="reminder-banners">
      {visible.map((r) => (
        <div
          key={r.class_id}
          className="hifz-card grain bg-forest-700 text-sand-100 border-forest-700 flex flex-wrap items-center justify-between gap-3"
          data-testid={`reminder-${r.class_id}`}
        >
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-sand-100/15 grid place-items-center">
              <Video className="h-5 w-5" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-gold-400 font-semibold">{t("classReminderBanner.halaqaStarting", { when: formatCountdown(r.minutes_until, t) })}</div>
              <div className="mt-0.5 font-display font-semibold text-lg">{r.class_name}</div>
              <div className="text-xs text-sand-100/70 mt-0.5 flex items-center gap-2">
                <Clock className="h-3 w-3" />
                {new Date(r.starts_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                {r.teacher_name && <span>{t("classReminderBanner.with", { teacher_name: r.teacher_name })}</span>}
                {r.zoom_passcode && <span>{t("classReminderBanner.passcode")} <strong className="text-gold-400">{r.zoom_passcode}</strong></span>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {r.zoom_url ? (
              <button onClick={() => join(r)} className="btn-accent text-sm" data-testid={`join-zoom-${r.class_id}`}>
                <Video className="h-4 w-4" /> {t("halaqa.joinZoom")}
              </button>
            ) : (
              <span className="text-xs text-sand-100/60">{t("classReminderBanner.noZoomLinkYet")}</span>
            )}
            <button
              onClick={() => setDismissed({ ...dismissed, [r.class_id]: true })}
              className="p-2 rounded-lg hover:bg-sand-100/10 transition"
              data-testid={`dismiss-${r.class_id}`}
              aria-label={t("classReminderBanner.dismiss")}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
